'use client';

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type DetailsTabKey = 'description' | 'reviews' | 'downloads' | 'compatibility';

type DetailsTabsState = { active: DetailsTabKey; setActive: (key: DetailsTabKey) => void };

const DetailsTabsContext = createContext<DetailsTabsState | null>(null);

/**
 * The active tab of „Popis a parametre / Hodnotenia / Na stiahnutie“, shared by the tab bar and the
 * sections that belong to one tab (the reviews overview under the description). Per page, so a
 * client navigation to another product starts on its first available tab again.
 */
export function DetailsTabsProvider({
  children,
  initialTab = 'description',
}: {
  children: ReactNode;
  initialTab?: DetailsTabKey;
}) {
  const [active, setActive] = useState<DetailsTabKey>(initialTab);
  const value = useMemo(() => ({ active, setActive }), [active]);
  return <DetailsTabsContext.Provider value={value}>{children}</DetailsTabsContext.Provider>;
}

export function useDetailsTabs() {
  const state = useContext(DetailsTabsContext);
  if (!state) throw new Error('useDetailsTabs must be used inside DetailsTabsProvider');
  return state;
}

/**
 * Renders the children only while `tab` is active – Figma shows the reviews overview (115:1311)
 * in the „Popis a parametre“ state only (hidden in 146:1267, 146:3328 and 146:8573).
 */
export function WithDetailsTab({ tab, children }: { tab: DetailsTabKey; children: ReactNode }) {
  const { active } = useDetailsTabs();
  return active === tab ? children : null;
}
